/* External Dependencies */
import _get from 'lodash/get';
import { filter, pipe } from 'rxjs/operators';

/* Core Dependencies */
import { AbstractPlugin, MediaPlayerEvents } from 'mlb-media-player';
import AnalyticsPluginEvents from '../analytics-plugin-events';
import ScriptLoader from '../../utils/script-loader';

/**
 *
 * @module ConvivaPlugin
 * @desc
 * @class ConvivaPlugin
 *
 */

class ConvivaPlugin extends AbstractPlugin {

    constructor(config, observationCenter) {

        super(config, observationCenter);

        this.playerSubject = this.observationCenter.playerSubject;

        this.pluginSubject = this.observationCenter.pluginSubject;

        // Conviva Asset Name
        this.assetName = _get(this.config, 'conviva.assetName', '');

        // Conviva SDK Library
        this.sdk = _get(this.config, 'conviva.sdk', '');

        // Conviva Customer Key @string
        this.customerKey = _get(this.config, 'conviva.customerKey', '');

        // Conviva Application Name @string
        this.applicationName = _get(this.config, 'conviva.applicationName', '');

        // Conviva Custom @string
        this.custom = _get(this.config, 'conviva.custom', '');

        // Conviva Gateway URL @string
        this.gatewayUrl = _get(this.config, 'conviva.gatewayUrl', '');

        // Track Live Videos
        this.trackLiveVideos = _get(this.config, 'trackLiveVideos', true);

        // Track VOD Videos
        this.trackVodVideos = _get(this.config, 'trackVodVideos', false);

        // Track if content is Live or VOD
        this.contentIsLive = null;

        // Conviva Core and HTML5 Native Implementation @object
        this.Conviva = null;

        // Conviva Content Session Key @string
        this.contentSessionKey = null;

        // Conviva System Factory @object
        this.systemFactory = null;

        // Conviva Client @object
        this.client = null;

        // Conviva Player Manager @object
        this.playerStateManager = null;

        // Conviva is ready to track
        this.trackingInitialized = null;

        // Conviva Viewer ID
        this.viewerId = null;

        // Conviva monitoring session
        this.html5PlayerInterface = null;

    }

    /**
     *
     * @desc Subscribe to the ObservationCenter messages
     *
     */
    subscribeToMessages() {

        const mediaPlayerEvents = MediaPlayerEvents.MEDIA_PLAYER;
        const hlsPlayerEvents = MediaPlayerEvents.HLSJS;

        const mediaItemObservable = this.playerSubject.pipe(filter((event) => event.type === mediaPlayerEvents.MEDIA_ITEM_SET)).subscribe((data) => {

            // If tracking initialized
            if (this.trackingInitialized) {

                // Reset conviva
                this.resetTracking();

            }

            if (this.trackVodVideos) {

                // Init tracking
                this.initTracking();

            }

        });

        // Level Loaded subscription
        const levelLoadedSubscription = this.playerSubject.pipe(filter((event) => event.type === hlsPlayerEvents.LEVEL_LOADED)).subscribe((data) => {

            // Content is live
            const isLive = _get(data, 'details.live', false);

            // Media Stream Type Changed
            const mediaStreamTypeChanged = this.contentIsLive !== isLive;

            // Update content is live
            this.contentIsLive = isLive;

            //@TODO
            //How can we do this in conjunction with this.trackLiveVideos and this.trackVodVideos?
            if (this.trackingInitialized && mediaStreamTypeChanged) {

                // Update tracking metadata
                this.updateTrackingMetadata();

            } else if (this.trackLiveVideos && !this.trackingInitialized && this.contentIsLive) {

                // Init tracking
                this.initTracking();

            }

        });

        // Append media item subscription and level loaded subscription into subscriptions to get it released when the plugin is destroyed
        this.subscriptions.push(mediaItemObservable);
        this.subscriptions.push(levelLoadedSubscription);

    }

    /**
     *
     * @desc Unsubscribe from event streams
     * @private
     *
     */
    unsubscribe() {

        this.subscriptions.forEach((subscription) => {

            subscription.unsubscribe();

        });

    }

    setMediaPlayer(mediaPlayer) {

        /* If we have a mediaPlayer, we need to unsubscribe from the event streams
            because we're going to resubscribe shorlty for the new player */
        if (this.mediaPlayer) {

            this.unsubscribe();

        }

        // Set so init and subscribeToMessages has a reference
        this.mediaPlayer = mediaPlayer;

        // init the conviva plugin
        return this.init().then(() => {

            this.subscribeToMessages();

            // Let the super know (this also calls pluginReady())
            super.setMediaPlayer(this.mediaPlayer);

        });

    }

    /**
     *
     * @desc Initialize the plugin.
     *
     */
    init() {

        const env = _get(window, 'mediaConfig.app.runTimeConfig', 'prod');

        return Promise
            // Load Conviva Core script
            .resolve(ScriptLoader.getAPI(_get(this.sdk, 'core.' + env), 'Conviva'))
            .then((Conviva) => {

                // Load Conviva HTML5 native implementation script
                return ScriptLoader.getAPI(_get(this.sdk, 'html5Impl.' + env), 'Conviva');

            })
            .then((Conviva) => {

                // Store Conviva Core and HTML5 Native Implementation
                this.Conviva = Conviva;

                // On script loaded
                this._onScriptLoaded();

            })
            .catch((error) => {

                this.handleError(error, AnalyticsPluginEvents.ANALYTICS_PLUGIN_ERROR, true);

            });

    }

    /**
     *
     * @desc Called when tracking is not initialized.
     *
     */
    initTracking() {

        try {

            // Create session
            this.createTrackingSession();

            // Attach Tracker to player
            this.attachTrackerToPlayer();

            // Send ANALYTICS_LOADED message
            this.pluginSubject.next({ type: AnalyticsPluginEvents.ANALYTICS_LOADED });

            // Tracking has been initialized
            this.trackingInitialized = true;

        } catch (error) {

            this.handleError(error, AnalyticsPluginEvents.ANALYTICS_ERROR);

        }

    }

    /**
     *
     * @desc Get the Tracking Client.
     *
     */
    getTrackingClient() {

        if (!this.client) {

            // Create system settings
            let systemSettings = new Conviva.SystemSettings();

            // Create system interfaces
            let systemInterface = new Conviva.SystemInterface(
                new Conviva.Impl.Html5Time(),
                new Conviva.Impl.Html5Timer(),
                new Conviva.Impl.Html5Http(),
                new Conviva.Impl.Html5Storage(),
                new Conviva.Impl.Html5Metadata(),
                new Conviva.Impl.Html5Logging()
            );

            // Create system factory
            this.systemFactory = new Conviva.SystemFactory(systemInterface, systemSettings);

            // Create client settings
            let clientSettings = new Conviva.ClientSettings(this.customerKey);

            // Valiate gateway url
            if (this.gatewayUrl) {

                // Set client gateway url
                clientSettings.gatewayUrl = this.gatewayUrl;

            }

            // Create client
            this.client = new Conviva.Client(clientSettings, this.systemFactory);

        }

        return this.client;

    }

    /**
     *
     * @desc Create the Tracking Session after creating the Tracking Client.
     *
     */
    createTrackingSession() {

        // Retrieve content metadata.
        let contentMetadata = this.createTrackingMetadata();

        // Create session.
        this.contentSessionKey = this.getTrackingClient().createSession(contentMetadata);

        // Validate session key.
        if (this.contentSessionKey === Conviva.Client.NO_SESSION_KEY) {

            throw new Error('Unable to create Conviva Session');

        }

        // Retrieve player manager
        this.playerStateManager = this.getTrackingClient().getPlayerStateManager();

        // Create HTML 5 player interface.
        this.html5PlayerInterface = new Conviva.Impl.Html5PlayerInterface(this.playerStateManager, _get(this.mediaPlayer, 'player.videoEl'), this.systemFactory);

    }

    destroyTrackingSession(sessionKey) {

        // Terminate the existing Conviva monitoring session represented by sessionKey
        this.html5PlayerInterface.cleanup();
        this.html5PlayerInterface = null;

        this.client.cleanupSession(sessionKey);

        // Reset content session key
        this.contentSessionKey = null;

    }

    /**
     *
     * @desc Create Tracking Metadata after the Tracking Session is created.
     *
     */
    createTrackingMetadata() {

        // Conviva Content Metadata
        let trackingMetaData = new Conviva.ContentMetadata();

        // Set application name
        trackingMetaData.applicationName = this.applicationName;

        // Set asset name
        trackingMetaData.assetName = this.assetName;

        // Set stream type
        trackingMetaData.streamType = !this.trackingInitialized
            ? Conviva.ContentMetadata.StreamType.UNKNOWN
            : this.contentIsLive ? Conviva.ContentMetadata.StreamType.LIVE
                : Conviva.ContentMetadata.StreamType.VOD;

        // Set viewer id. if we have it available
        if (this.viewerId) {

            trackingMetaData.viewerId = this.viewerId;

        }

        // Set stream url
        trackingMetaData.streamUrl = _get(this.mediaPlayer, 'player.playerTypeConfig.mediaItem.src');

        // Validate custom
        if (this.custom) {

            // Set custom
            trackingMetaData.custom = this.custom;

        }

        return trackingMetaData;

    }

    /**
     *
     * @desc Update existing session tracking metadata
     *
     */
    updateTrackingMetadata() {

        // Validate client
        if (this.client) {

            if (this.contentSessionKey !== Conviva.Client.NO_SESSION_KEY
                && Number.isInteger(this.contentSessionKey)) {

                // Retrieve content metadata.
                let contentMetadata = this.createTrackingMetadata();

                // Update the current session with metadata changes
                this.client.updateContentMetadata(this.contentSessionKey, contentMetadata);

            }

        }

    }

    /**
     *
     * @desc Attach Tracker to Player after creating Tracking Client and Tracking Session.
     *
     */
    attachTrackerToPlayer() {

        if (!this.client.isPlayerAttached(this.contentSessionKey)) {

            // Attach player to client
            this.getTrackingClient().attachPlayer(this.contentSessionKey, this.playerStateManager);

        }

    }

    /**
     *
     * @desc Reset tracking
     *
     */
    resetTracking() {

        // Reset tracking initialized
        this.trackingInitialized = false;

        // Reset content is live
        this.contentIsLive = null;

        // Reset asset name
        this.assetName = _get(this.config, 'conviva.assetName', '');

        // Reset viewer id
        this.viewerId = null;

        // Validate client
        if (this.client) {

            if (this.contentSessionKey !== Conviva.Client.NO_SESSION_KEY
                && Number.isInteger(this.contentSessionKey)) {

                if (this.client.isPlayerAttached(this.contentSessionKey)) {

                    // Detach player
                    this.client.detachPlayer(this.contentSessionKey);

                }

                /**
                 * Validate content session key
                 * Session key is represented by an integer (0 included)
                 */

                // Cleanup session
                this.destroyTrackingSession(this.contentSessionKey);

            }

            // Validate player state manager
            if (this.playerStateManager) {

                // Reset player state manager
                this.playerStateManager.reset();

            }

        }

    }

    destroyConviva() {

        // reset client and system Factory
        // Release client
        if (this.client) {

            this.client.release();

        }

        // Release player state manager
        if (this.playerStateManager) {

            this.client.releasePlayerStateManager(this.playerStateManager);
            this.playerStateManager = null;

        }

        // Release system factory
        if (this.systemFactory) {

            this.systemFactory.release();

            // Set system factory to null
            this.systemFactory = null;

        }

    }

    /**
    *
    * @desc Set Tracking Metadata
    * @param {string} assetName
    * @param {string} {optional} viewerId
    */
    setTrackingMetadata(assetName, viewerId = null) {

        // If we don't have an asset name
        if ((!assetName && !this.assetName)) {

            let error = new Error('Missing Conviva Asset Name');

            this.handleError(error, AnalyticsPluginEvents.ANALYTICS_ERROR);

        }

        // Set asset name
        this.assetName = (assetName || this.assetName);

        // Set viewer id
        this.viewerId = viewerId;

    }

    /**
    *
    * @desc Handle Error
    * @param {object} error
    * @param {string} type
    * @param {boolean} {optional} fatal
    */
    handleError(error, type, fatal = false) {

        // Get error message
        let errorMessage = _get(error, 'message');

        if (this.Conviva && errorMessage) {

            const fatalStatus = _get(this.Conviva, 'Client.ErrorSeverity.FATAL');
            const warningStatus = _get(this.Conviva, 'Client.ErrorSeverity.WARNING');

            // Retrieve severity
            let severity = (fatal ? fatalStatus : warningStatus) || 'UNKNOWN';

            // Validate player state manager
            if (this.playerStateManager) {

                // Send error
                this.playerStateManager.sendError(errorMessage, severity);

            } else if (this.contentSessionKey) { // Validate session

                // Report error
                this.getTrackingClient().reportError(this.contentSessionKey, errorMessage, severity);

            }

        }

        // Reset Conviva
        this.resetTracking();

        // Inform PluginManager of error
        this.pluginSubject.next({
            type: type,
            error: error,
        });

        // Destroy if error is fatal
        if (fatal) {

            this.destroy();

        }

    }

    /**
     *
     * @desc Fires when the Conviva library scripts has loaded.
     *
     */
    _onScriptLoaded() {

        // Conviva Content session key.
        this.contentSessionKey = Conviva.Client.NO_SESSION_KEY;

        // Send ANALYTICS_PLUGIN_LOADED message
        this.pluginSubject.next({ type: AnalyticsPluginEvents.ANALYTICS_PLUGIN_LOADED });

    }

    /**
     *
     * @desc Called when the analytics manager is destroyed.
     *
     */
    disposeContext() {

        if (this.context) {

            this.context.dispose();

        }

    }

    /**
     *
     * @desc Destroy the analytics manager.
     *
     */
    destroy() {

        this.destroyConviva();

        this.disposeContext();

        super.destroy();

    }

    attachLogger(logger) {

        this.logger = logger;

    }

    /**
     *
     * @desc Utility function for returning the name of the class.
     * @return string The class type.
     *
     */
    toString() {

        return 'ConvivaPlugin';

    }

}

export default ConvivaPlugin;
